import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Auth } from 'aws-amplify';
import {
  acquireRealtimeService,
  getRealtimeService,
  releaseRealtimeService,
} from './RealtimeService';
import { getRealtimeWsUrl, isRealtimeWsEnabled } from './config';

const RealtimeContext = createContext({
  status: 'idle',
  isConnected: false,
  subscribe: () => () => {},
  subscribeChannels: () => {},
  service: null,
});

function resolveOrgIdentity() {
  const platformRole = localStorage.getItem('platform_role');
  const organizationUlid =
    platformRole === 'broker'
      ? localStorage.getItem('brokerUlid') || localStorage.getItem('loggedUserUlid')
      : localStorage.getItem('loggedUserUlid') || localStorage.getItem('organization_ulid');
  const userUlid = localStorage.getItem('user_ulid') || localStorage.getItem('loggedUserUlid');
  const buyer = localStorage.getItem('associatedBroker');
  const extraOrgUlids = [];
  if (buyer) extraOrgUlids.push(buyer);
  return { organizationUlid, userUlid, extraOrgUlids };
}

export function RealtimeProvider({ children, enabled, onSessionExpired }) {
  const [status, setStatus] = useState('idle');
  const serviceRef = useRef(null);
  const onSessionExpiredRef = useRef(onSessionExpired);
  onSessionExpiredRef.current = onSessionExpired;

  const isLoggedIn = typeof window !== 'undefined' && localStorage.getItem('isLoggedIn') === '1';
  const flagEnabled = enabled ?? isRealtimeWsEnabled();

  useEffect(() => {
    if (!flagEnabled || !isLoggedIn) {
      serviceRef.current = null;
      setStatus('idle');
      return undefined;
    }

    const service = acquireRealtimeService({
      enabled: true,
      wsUrl: getRealtimeWsUrl(),
      getAccessToken: async () => {
        try {
          const session = await Auth.currentSession();
          return session.getAccessToken().getJwtToken();
        } catch {
          return localStorage.getItem('accessToken');
        }
      },
      getIdentity: resolveOrgIdentity,
      onSessionExpired: (reason) => {
        onSessionExpiredRef.current?.(reason);
      },
    });
    serviceRef.current = service;
    const unsubStatus = service.onStatusChange(() => setStatus(service.status));
    service.connect();
    setStatus(service.status);

    return () => {
      unsubStatus();
      // StrictMode: release only decrements; socket closes when refCount hits 0
      releaseRealtimeService();
      serviceRef.current = null;
    };
    // Intentionally omit onSessionExpired — held in a ref to avoid reconnect churn
  }, [flagEnabled, isLoggedIn]);

  const subscribe = useCallback((eventType, handler) => {
    if (!flagEnabled || !isLoggedIn) return () => {};

    let unsubscribe = () => {};
    let timer = null;
    let cancelled = false;

    const bind = () => {
      const service = serviceRef.current || getRealtimeService();
      if (!service) return false;
      unsubscribe = service.subscribe(eventType, handler);
      return true;
    };

    // Child effects run before the provider effect that creates the socket.
    // Retry until the shared service exists so bid toasts are not dropped.
    if (!bind()) {
      timer = setInterval(() => {
        if (cancelled) return;
        if (bind()) {
          clearInterval(timer);
          timer = null;
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      unsubscribe();
    };
  }, [flagEnabled, isLoggedIn]);

  const subscribeChannels = useCallback((channels) => {
    const service = serviceRef.current || getRealtimeService();
    service?.subscribeChannels(channels);
  }, []);

  const value = useMemo(
    () => ({
      status,
      isConnected: status === 'open',
      subscribe,
      subscribeChannels,
      service: serviceRef.current,
    }),
    [status, subscribe, subscribeChannels]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime() {
  return useContext(RealtimeContext);
}

/**
 * Subscribe to a realtime event type. Handler kept in a ref to avoid resubscribe churn.
 * @param {string|string[]} eventType
 * @param {(envelope: import('./types').RealtimeEnvelope) => void} handler
 * @param {unknown[]} [deps]
 */
export function useRealtimeEvent(eventType, handler, deps = []) {
  const { subscribe } = useRealtime();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    return subscribe(eventType, (envelope) => handlerRef.current(envelope));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, Array.isArray(eventType) ? eventType.join(',') : eventType, ...deps]);
}

export function useRealtimeStatus() {
  const { status, isConnected } = useRealtime();
  return { status, isConnected };
}

export const useWebSocket = useRealtime;
export const WebSocketProvider = RealtimeProvider;
